import Cookies from 'cookies-js';

import { wipeBrazeUser } from './braze';

export const REAUTH_REQUIRED_EVENT = 'utr:reauth-required';
export const REAUTH_SIGNAL_CODE = 'reauth_required';
export const REAUTH_CHALLENGE_TOKEN = 'account_deleted';
export const REAUTH_REDIRECT_DELAY_MS = 3000;
export const REAUTH_MODAL_TITLE = 'Your session has ended';
export const REAUTH_MODAL_MESSAGE = 'Your account is no longer active. Please sign in again.';

// Server-side reasons carried alongside REAUTH_SIGNAL_CODE (UT_API MiddlewareConstants.Reauth).
export const REAUTH_REASON_ACCOUNT_DELETED = 'account_deleted';
export const REAUTH_REASON_SESSION_REVOKED = 'session_revoked';

// A device-cap eviction (ENG-2593) ends this device's session while the account stays active and paid,
// so it must not reuse the deleted-account copy above — that reads as deactivation and invites a
// support contact. Signing in again here is expected to work immediately.
export const SESSION_REVOKED_MODAL_TITLE = 'You have been signed out on this device';
export const SESSION_REVOKED_MODAL_MESSAGE = 'This account reached its limit for signed-in devices, so the least recently used device was signed out. Your account is still active — sign in again to keep using it on this device.';
export const SESSION_REVOKED_CONFIRM_LABEL = 'Got it';

const JWT_COOKIE = 'jwt';
const LOGIN_PATH = '/login';
const UNAUTHORIZED_STATUS = 401;

let latchedReason;
let reauthTriggered = false;

const isBrowser = () => typeof window !== 'undefined';

const getChallenge = (response) => {
    const challenge = response?.headers?.['www-authenticate'];
    return typeof challenge === 'string' ? challenge : null;
};

// The server (UT-11305) answers an authenticated request from a soft-deleted member with a 401
// carrying a machine-readable re-auth signal: a JSON body { code: 'reauth_required' } and a
// WWW-Authenticate challenge containing 'account_deleted'. ENG-2593 added a second reason,
// 'session_revoked', on the same code. A plain expired/invalid token 401 does NOT carry this, so
// keying strictly on the signal keeps ordinary 401s on their current behavior.
export const isReauthSignal = (error) => {
    const response = error?.response;
    if (response?.status !== UNAUTHORIZED_STATUS) {
        return false;
    }
    if (response.data?.code === REAUTH_SIGNAL_CODE) {
        return true;
    }
    const challenge = getChallenge(response);
    if (!challenge) {
        return false;
    }
    return challenge.includes(REAUTH_CHALLENGE_TOKEN) || challenge.includes(REAUTH_REASON_SESSION_REVOKED);
};

// Which reason the server gave, so the handler can tell "your account is gone" apart from "this
// device was signed out". Falls back to the challenge header for responses whose body was not parsed
// as JSON (a non-default axios responseType), and returns null when the reason is absent or
// unrecognized — callers treat that as the deleted-account case, the safer of the two.
export const getReauthReason = (error) => {
    if (!isReauthSignal(error)) {
        return null;
    }
    const { response } = error;
    const bodyReason = response.data?.reason;
    if (bodyReason === REAUTH_REASON_SESSION_REVOKED || bodyReason === REAUTH_REASON_ACCOUNT_DELETED) {
        return bodyReason;
    }
    const challenge = getChallenge(response);
    if (challenge?.includes(REAUTH_REASON_SESSION_REVOKED)) {
        return REAUTH_REASON_SESSION_REVOKED;
    }
    if (challenge?.includes(REAUTH_REASON_ACCOUNT_DELETED)) {
        return REAUTH_REASON_ACCOUNT_DELETED;
    }
    return null;
};

// A revoked or deleted session 401s every in-flight request at once, so many interceptors fire
// together; the guard collapses that burst into a single handling. It is keyed on the reason rather
// than being a one-shot: the session_revoked handler deliberately does not navigate, and a plain
// one-shot would then stay latched for the rest of the page session and swallow a later, genuine
// account_deleted. The login route is skipped so a stray signal cannot start a redirect loop.
export const triggerReauthRequired = (reason = null) => {
    if (!isBrowser()) {
        return;
    }
    if (window.location?.pathname?.startsWith(LOGIN_PATH)) {
        return;
    }
    if (reauthTriggered && latchedReason === reason) {
        return;
    }
    reauthTriggered = true;
    latchedReason = reason;
    window.dispatchEvent(new CustomEvent(REAUTH_REQUIRED_EVENT, { detail: { reason } }));
};

// Lets the listener replay a trigger that fired before it mounted (a 401 resolving in the gap
// before AppContent's effect attaches), so the one-shot can't latch with nothing having handled it.
export const isReauthPending = () => reauthTriggered;

export const getPendingReauthReason = () => latchedReason ?? null;

export const clearReauthSession = async () => {
    if (!isBrowser()) {
        return;
    }
    // Expire the now-invalid token FIRST — it is the security-critical step and must not sit behind
    // the best-effort Braze wipe, whose dynamic import can outrun the 3s hard redirect on a slow link.
    Cookies.expire(JWT_COOKIE);
    if (window.analytics) {
        window.analytics.reset();
    }
    try {
        window.fcWidget?.destroy();
    } catch {
        // ignore errors destroying the Freshchat widget
    }
    await wipeBrazeUser();
};

export const redirectToLogin = () => {
    if (isBrowser()) {
        window.location.href = LOGIN_PATH;
    }
};
